import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { LanguageToggle } from '@/components/LanguageToggle';
import { InstallButton } from '@/components/InstallPrompt';
import { SiteFooter } from '@/components/SiteFooter';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { useT } from '@/lib/i18n';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Play, MapPin, Camera, ShieldCheck, EyeOff, Volume2, Lock, Users, Check, ArrowRight } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1596789592210-e2e93ff08bc4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwaGlsaXBwaW5lcyUyMHN0cmVldCUyMGNvbW11dGUlMjB0cmljeWNsZXxlbnwwfHx8fDE3ODgwNTQ3OTR8MA&ixlib=rb-4.1.0&q=85';
const FAMILY_IMG = 'https://images.unsplash.com/photo-1548345680-f5475ea5df84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjB0cmFja2luZyUyMHBob25lJTIwbWFwfGVufDB8fHx8MTc4ODA1NDc5OHww&ixlib=rb-4.1.0&q=85';

const Step = ({ n, Icon, title, text }) => (
  <div className="rounded-2xl p-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }}>{n}</span>
      <Icon size={20} style={{ color: 'var(--primary)' }} />
    </div>
    <h3 className="font-bold" style={{ color: 'var(--text)' }}>{title}</h3>
    <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{text}</p>
  </div>
);

const Trust = ({ text }) => (
  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text)' }}>
    <Check size={18} style={{ color: 'var(--success)' }} /> {text}
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const t = useT();
  const onboarded = useApp((s) => s.onboarded);
  const familyMode = useApp((s) => s.familyMode);

  useEffect(() => { api.track('landing_viewed'); }, []);

  const startRide = () => { api.track('landing_start_ride'); navigate(onboarded ? '/app/start' : '/onboarding'); };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <div className="sticky top-0 z-30" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="mx-auto max-w-5xl px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <div className="hidden sm:block"><ThemeSwitcher compact /></div>
            <InstallButton className="!min-h-[40px] !px-4 text-sm" label={t('l_install_short')} testid="landing-install-button" />
            <button data-testid="landing-open-app-button" onClick={() => navigate('/app')} className="inline-flex items-center rounded-full px-4 min-h-[40px] text-sm font-semibold border" style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}>Open</button>
          </div>
        </div>
      </div>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, var(--surface-alt), var(--background) 60%)' }} />
        <div className="relative mx-auto max-w-5xl px-4 pt-10 pb-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: 'var(--text)' }}>{t('l_hero')}</h1>
            <p className="mt-4 text-base sm:text-lg" style={{ color: 'var(--text-muted)' }}>{t('l_sub')}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="landing-start-ride-button" onClick={startRide} className="inline-flex items-center gap-2 rounded-full font-bold min-h-[52px] px-6 text-base transition-colors" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                <Play size={20} /> {t('l_start')}
              </button>
              <a href="#how" data-testid="landing-how-it-works" className="inline-flex items-center gap-2 rounded-full font-semibold min-h-[52px] px-6 border" style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}>{t('l_how')}</a>
            </div>
            <div className="mt-6 space-y-2">
              <Trust text={t('l_trust1')} /><Trust text={t('l_trust2')} /><Trust text={t('l_trust3')} />
            </div>
            <div className="mt-5 sm:hidden"><ThemeSwitcher compact /></div>
          </div>
          <div className="rounded-3xl overflow-hidden border" style={{ borderColor: 'var(--border)', boxShadow: 'var(--shadow)' }}>
            <img src={HERO_IMG} alt="Commute in the Philippines" className="w-full h-64 md:h-80 object-cover" />
          </div>
        </div>
      </section>

      <section id="how" className="mx-auto max-w-5xl px-4 py-10">
        <h2 className="text-2xl font-extrabold mb-5" style={{ color: 'var(--text)' }}>{t('l_how_title')}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Step n={1} Icon={Play} title={t('l_s1t')} text={t('l_s1b')} />
          <Step n={2} Icon={MapPin} title={t('l_s2t')} text={t('l_s2b')} />
          <Step n={3} Icon={Camera} title={t('l_s3t')} text={t('l_s3b')} />
          <Step n={4} Icon={ShieldCheck} title={t('l_s4t')} text={t('l_s4b')} />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-6">
        <h2 className="text-2xl font-extrabold mb-5" style={{ color: 'var(--text)' }}>{t('l_tools')}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-2xl p-5 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <EyeOff size={22} style={{ color: 'var(--primary)' }} />
            <h3 className="font-bold mt-2" style={{ color: 'var(--text)' }}>{t('l_quiet')}</h3>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{t('l_quiet_b')}</p>
          </div>
          <div className="rounded-2xl p-5 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Volume2 size={22} style={{ color: 'var(--danger)' }} />
            <h3 className="font-bold mt-2" style={{ color: 'var(--text)' }}>{t('l_loud')}</h3>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{t('l_loud_b')}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-6">
        <div className="rounded-2xl p-5 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }}>
          <h2 className="text-xl font-extrabold" style={{ color: 'var(--text)' }}>{t('l_free')}</h2>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
            {[
              'Someone you trust always knows where you are',
              'Share your live trip with one tap',
              'Silent SOS for when you can’t speak up',
              'A loud alarm to scare off trouble',
              'A saved photo of your vehicle and plate',
              'One-tap “I’m Safe” to reassure your family',
              'Keeps working even on a weak signal',
              'No sign-up needed — start in seconds',
            ].map((f) => <Trust key={f} text={f} />)}
          </div>
        </div>
      </section>

      {familyMode && (
      <section className="mx-auto max-w-5xl px-4 py-6">
        <div className="rounded-2xl overflow-hidden border grid md:grid-cols-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="p-6">
            <div className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--primary)' }}><Users size={18} /> {t('l_family')}</div>
            <h2 className="text-2xl font-extrabold mt-2" style={{ color: 'var(--text)' }}>{t('l_family_h')}</h2>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>{t('l_family_b')}</p>
            <p className="text-3xl font-extrabold mt-4" style={{ color: 'var(--text)' }}>₱129<span className="text-base font-medium" style={{ color: 'var(--text-muted)' }}>{t('l_month')}</span></p>
            <button onClick={() => navigate('/app/account')} className="mt-4 inline-flex items-center gap-2 rounded-full font-semibold min-h-[48px] px-5 border" style={{ borderColor: 'var(--primary)', color: 'var(--primary)', background: 'var(--surface)' }}>{t('l_learn')} <ArrowRight size={18} /></button>
          </div>
          <img src={FAMILY_IMG} alt="Family tracking a loved one's ride" loading="lazy" className="w-full h-full min-h-[200px] object-cover" />
        </div>
      </section>
      )}

      <section className="mx-auto max-w-5xl px-4 py-6">
        <div className="rounded-2xl p-5 border flex items-start gap-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Lock size={22} style={{ color: 'var(--primary)' }} />
          <div>
            <h2 className="text-xl font-extrabold" style={{ color: 'var(--text)' }}>{t('l_privacy')}</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{t('l_privacy_b')}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-6">
        <h2 className="text-2xl font-extrabold mb-3" style={{ color: 'var(--text)' }}>{t('l_faq')}</h2>
        <Accordion type="single" collapsible className="rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <AccordionItem value="1" className="px-4"><AccordionTrigger>{t('l_faq1q')}</AccordionTrigger><AccordionContent>{t('l_faq1a')}</AccordionContent></AccordionItem>
          <AccordionItem value="2" className="px-4"><AccordionTrigger>{t('l_faq2q')}</AccordionTrigger><AccordionContent>{t('l_faq2a')}</AccordionContent></AccordionItem>
          <AccordionItem value="3" className="px-4"><AccordionTrigger>{t('l_faq3q')}</AccordionTrigger><AccordionContent>{t('l_faq3a')}</AccordionContent></AccordionItem>
        </Accordion>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10 text-center">
        <h2 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('l_install_title')}</h2>
        <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>{t('l_install_sub')}</p>
        <div className="mt-4 flex justify-center gap-3">
          <InstallButton label={t('l_install')} />
          <button data-testid="landing-open-app-button-2" onClick={() => navigate('/app')} className="inline-flex items-center gap-2 rounded-full font-semibold min-h-[48px] px-5 border" style={{ borderColor: 'var(--primary)', color: 'var(--primary)', background: 'var(--surface)' }}>Open KilalaRide</button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
